import React, { useMemo, useState } from "react";

import { Button, ButtonGroup, Field, Icon, Input, Modal, Segmented } from "../components/ui";

/**
 * Start a Roam meeting and share it in the channel — RC master admins only.
 *
 * Two modes, and they take different paths on the backend:
 *
 *   Now       `meeting.link.create` — a link with no time window, posted
 *             into the channel immediately.
 *   Schedule  `calendar.event.create` — writes the event on the host's
 *             connected calendar, has Roam attach a meeting link to it, and
 *             **emails an invitation to everyone in the conversation**. The
 *             link is posted in the channel as well.
 *
 * Scheduling only became real on 2026-10-01, when Alex granted the org key
 * `calendar:write`. Before that it set start/end on a link that nothing read,
 * so it wrote a time into a message and did nothing else — which is exactly
 * what he noticed. The duration control is back because a calendar event
 * needs an end time.
 *
 * ⚠️ The scheduled path is the ONLY place this feature emails a merchant.
 * That is intended — it is a calendar invitation — but it is worth knowing
 * before changing who the attendees are.
 *
 * Every control here is NATIVE (date, time, time zone) rather than the
 * Fontaine DatePicker and Select. Both of those position their panel against
 * the page: inside a modal the DatePicker's panel was clipped by the scroll
 * container so the calendar icon appeared dead, and the Select's listbox
 * opened at the bottom of the viewport outside the dialog altogether
 * (Alex, 2026-10-01). A native control's popup is drawn by the OS and always
 * lands in the right place. It also makes the date and time fields identical
 * in width with one shared indicator.
 *
 * There is still deliberately **no audio-only option**: the Roam endpoints
 * have no media parameter, so participants choose camera and mic on joining.
 */

/**
 * A short, honest timezone list: whatever the browser reports first, then the
 * zones Retail Circle actually trades across. Not a full IANA picker —
 * nobody choosing a meeting slot wants to scroll 400 entries.
 */
const COMMON_ZONES = [
  "Europe/London",
  "Europe/Paris",
  "Europe/Zurich",
  "Asia/Kolkata",
  "Asia/Dubai",
  "Asia/Hong_Kong",
  "Asia/Singapore",
  "America/New_York",
  "America/Los_Angeles",
  "UTC",
];

const DURATIONS = [
  { value: "30", label: "30m" },
  { value: "45", label: "45m" },
  { value: "60", label: "1h" },
  { value: "90", label: "1h 30m" },
];

const localZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch (error) {
    return "UTC";
  }
};

/** "Asia/Kolkata" → "Asia/Kolkata (GMT+5:30)", so the offset is visible. */
const zoneLabel = (zone) => {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: zone,
      timeZoneName: "shortOffset",
    }).formatToParts(new Date());
    const offset = parts.find((part) => part.type === "timeZoneName")?.value;

    return offset ? `${zone} (${offset})` : zone;
  } catch (error) {
    return zone;
  }
};

/** Today as YYYY-MM-DD, for the native date input's `min`. */
const todayValue = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${now.getFullYear()}-${month}-${day}`;
};

/**
 * Turn the picked day, the typed time and the chosen zone into an absolute
 * instant.
 *
 * The browser would read "2026-10-02T09:00" in ITS zone, which is wrong the
 * moment the organiser picks a different one. So we measure that zone's
 * offset at that date (which handles daylight saving, unlike a fixed table)
 * and subtract it.
 */
const toIso = (date, time, zone, minutesToAdd = 0) => {
  if (!date) return null;

  const [year, month, day] = String(date).split("-").map(Number);
  const [hours, minutes] = String(time || "09:00").split(":").map(Number);

  if (!year || !month || !day) return null;

  const wallClock = Date.UTC(
    year,
    month - 1,
    day,
    Number.isFinite(hours) ? hours : 9,
    (Number.isFinite(minutes) ? minutes : 0) + minutesToAdd
  );

  try {
    // What that same instant looks like as a wall clock in the target zone;
    // the difference is the offset to remove.
    const asZoned = new Date(
      new Date(wallClock).toLocaleString("en-US", { timeZone: zone })
    ).getTime();
    const asUtc = new Date(
      new Date(wallClock).toLocaleString("en-US", { timeZone: "UTC" })
    ).getTime();

    return new Date(wallClock - (asZoned - asUtc)).toISOString();
  } catch (error) {
    // Unknown zone: treat the typed time as UTC rather than failing. The
    // backend validates the zone separately and falls back the same way.
    return new Date(wallClock).toISOString();
  }
};

const MeetingDialog = ({ channelName, onClose, onCreate }) => {
  const [name, setName] = useState("");
  const [when, setWhen] = useState("now");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("09:00");
  const [zone, setZone] = useState(localZone);
  const [duration, setDuration] = useState("30");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const zoneOptions = useMemo(() => {
    const unique = [localZone(), ...COMMON_ZONES].filter(
      (value, index, all) => all.indexOf(value) === index
    );

    return unique.map((value) => ({ value, label: zoneLabel(value) }));
  }, []);

  const scheduled = when === "later";
  const canSubmit = name.trim() !== "" && (!scheduled || Boolean(date)) && !busy;

  const submit = async () => {
    if (!canSubmit) return;

    setBusy(true);
    setError(null);

    try {
      await onCreate({
        name: name.trim(),
        start: scheduled ? toIso(date, time, zone) : null,
        end: scheduled ? toIso(date, time, zone, Number(duration)) : null,
        timezone: scheduled ? zone : null,
      });
      onClose();
    } catch (requestError) {
      // The API answers 422 with Roam's own reason — a missing scope, a host
      // who is not a workspace member, a channel with no group. All operator
      // -fixable, so show the text instead of a generic failure.
      setError(
        requestError?.response?.data?.message ||
          "Could not create the meeting link. Please try again."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      variant="auto"
      size="sm"
      className="rc-chat-meeting-modal"
      title="Start a meeting"
      description={channelName ? `The link is posted in ${channelName}` : undefined}
      footer={
        <ButtonGroup align="end">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit} loading={busy}>
            {scheduled ? "Schedule and share" : "Start and share"}
          </Button>
        </ButtonGroup>
      }
    >
      <div className="rc-chat-meeting">
        <Field label="Meeting name" required>
          <Input
            placeholder="Stock review"
            value={name}
            maxLength={200}
            onChange={(event) => setName(event.target.value)}
            autoFocus
          />
        </Field>

        <Segmented
          label="When"
          value={when}
          onChange={setWhen}
          fullWidth
          options={[
            { value: "now", label: "Now" },
            { value: "later", label: "Schedule" },
          ]}
        />

        {scheduled && (
          <div className="rc-chat-meeting__schedule">
            <div className="rc-chat-meeting__row">
              <Field label="Date" required>
                <Input
                  type="date"
                  value={date}
                  min={todayValue()}
                  onChange={(event) => setDate(event.target.value)}
                />
              </Field>

              <Field label="Start time">
                <Input
                  type="time"
                  value={time}
                  onChange={(event) => setTime(event.target.value)}
                />
              </Field>
            </div>

            <Field label="Duration">
              <Segmented
                label="Duration"
                value={duration}
                onChange={setDuration}
                options={DURATIONS}
                fullWidth
              />
            </Field>

            {/* A NATIVE select, not the Fontaine one. The custom Select
                renders its listbox positioned against the page, so inside a
                modal it opened at the bottom of the viewport, outside the
                dialog entirely (Alex's screenshot, 2026-10-01) — the same
                class of problem as DatePicker's clipped panel. A native
                select's popup is drawn by the OS and is always placed
                correctly, which is why the date and time inputs are native
                here too. */}
            <Field label="Time zone" hint="The invitation and the posted message use this zone.">
              <select
                className="rc-chat-meeting__tz"
                value={zone}
                onChange={(event) => setZone(event.target.value)}
                aria-label="Time zone"
              >
                {zoneOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        )}

        {error && <p className="rc-chat-meeting__error">{error}</p>}

        {scheduled && (
          <p className="rc-chat-meeting__note">
            <Icon name="Info" size="xs" />
            <span>
              Creates the event on the host&apos;s calendar with the Roam link attached,
              and emails an invitation to everyone in this conversation.
            </span>
          </p>
        )}
      </div>
    </Modal>
  );
};

export default MeetingDialog;
